/*
  Attachment hotspot selection.

  The four circles overlaid on the attachment photographs are ARTWORK, not styled elements:
  100x100 <img>s, three of them the white ring (Group_33.png) and one the filled blue
  (Group_38.png). So "turn blue when clicked" means swapping the two image sources, not
  toggling a CSS class — which is why searching the DOM for circular elements by
  border-radius and background-colour finds nothing.

  On live the panel DOES change when a circle is clicked — but three of the four entries carried
  the identical "flat attachment / impact level 2/5" write-up, because that is all that was ever
  authored. Six of the seven attachment panels in `replo-air-s3`'s schema hold that same text.
  Fork and Bullet now have their own copy in snippets/pdp-hotspot-copy.liquid, so the panels no
  longer repeat themselves and this page is intentionally ahead of live.

  Copy is indexed by the circle's DOM position, and the four circles are absolutely positioned
  over the photograph, so index 1 is ROUND, 2 is FORK, 3 is FLAT, 4 is BULLET. That mapping
  lives in a comment in the snippet along with how to re-derive it. Do not reorder the array to
  match the visual arrangement: DOM order is what selects.

  It also swaps the attachment write-up beside them, from snippets/pdp-hotspot-copy.liquid.

  ⚠ FINDING THE PANEL IS THE FIDDLY PART, and getting it wrong is how an earlier attempt
  concluded the feature did not work at all: matching "an element containing both ATTACHMENT
  and IMPACT LEVEL" resolves to the **What's Included** block, several levels up, because that
  block contains the words "4 Premium Attachments". So the panel is found from the TITLE
  outwards — an element whose OWN text is a short string ending in ATTACHMENT — and then the
  nearest ancestor that also holds an "IMPACT LEVEL n/5" line. Never match on the ancestor.

  The two sources are discovered from the DOM rather than hardcoded: whichever src appears
  once is the active artwork, whichever appears more than once is the inactive artwork. That
  survives a Replo republish renaming the files, and it means this file has no knowledge of
  which product it is running on.
*/
(function () {
  'use strict';

  /*
    The `.png` is load-bearing, not decoration. `/Group_33|Group_38/` alone also matches
    Compression's `Group_385_f420bbca….png` gradient backdrops, which are not controls and are
    not even on this product. It does no damage today only because both copies share one src, so
    the two-distinct-sources check below bails out. Keep the extension anchored.
  */
  var MATCH = /Group_3[38]\.png/;

  function copyData() {
    var el = document.getElementById('pdp-hotspot-copy');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /*
    Child count, NOT counting <br>.

    The "one child at most" test is how a leaf of copy is told apart from a container, and it
    has to ignore line breaks or the panel breaks on the SECOND visit. Applying an entry sets
    innerHTML, so an entry with three <br>s leaves the body element holding three children.
    A plain `children.length > 1` then skips that element for ever: the title, level and group
    line keep updating and the body silently freezes on whichever entry was shown last. It
    looks like the copy for one attachment glued to the heading of another.
  */
  function childCount(el) {
    var n = 0;
    for (var i = 0; i < el.children.length; i++) {
      if (el.children[i].tagName !== 'BR') n++;
    }
    return n;
  }

  // The write-up beside the circles. Located from the TITLE outwards — see the header note.
  function findPanel() {
    var titles = [].slice.call(document.querySelectorAll('p,h1,h2,h3,h4,span,div')).filter(function (e) {
      if (childCount(e) > 1) return false;
      var t = (e.textContent || '').replace(/\s+/g, ' ').trim();
      if (t.length > 40 || !/attachment$/i.test(t)) return false;
      return e.getClientRects().length > 0;
    });
    for (var i = 0; i < titles.length; i++) {
      var el = titles[i].parentElement;
      for (var d = 0; d < 6 && el; d++, el = el.parentElement) {
        var lvl = null, grp = null, body = null;
        var kids = [].slice.call(el.querySelectorAll('p,span,div'));
        for (var k = 0; k < kids.length; k++) {
          var t = (kids[k].textContent || '').replace(/\s+/g, ' ').trim();
          if (childCount(kids[k]) > 1) continue;
          if (!lvl && /^impact level \d\/5$/i.test(t)) lvl = kids[k];
          else if (!grp && /\/\//.test(t) && t.length < 70) grp = kids[k];
          else if (!body && t.length > 60 && !/attachment$/i.test(t)) body = kids[k];
        }
        if (lvl) return { title: titles[i], level: lvl, groups: grp, body: body };
      }
    }
    return null;
  }

  function applyCopy(panel, entry) {
    if (!panel || !entry) return;
    if (entry.title && panel.title) panel.title.textContent = entry.title;
    if (entry.level && panel.level) panel.level.textContent = entry.level;
    if (entry.body && panel.body) panel.body.innerHTML = entry.body;
    if (entry.groups && panel.groups) panel.groups.textContent = entry.groups;
  }

  function groupsIn(root) {
    // Group circles by their nearest common ancestor that holds more than one, so two
    // separate hotspot sets on one page cannot fight each other.
    var imgs = [].slice.call(root.querySelectorAll('img')).filter(function (img) {
      return MATCH.test(img.getAttribute('src') || img.currentSrc || '');
    });
    var groups = [];
    imgs.forEach(function (img) {
      var found = groups.filter(function (g) { return g.parent.contains(img); })[0];
      if (found) { found.items.push(img); return; }
      var p = img.parentElement;
      // climb until the ancestor holds at least two of the circles
      for (var i = 0; i < 6 && p; i++) {
        var n = [].slice.call(p.querySelectorAll('img')).filter(function (x) {
          return MATCH.test(x.getAttribute('src') || x.currentSrc || '');
        }).length;
        if (n > 1) break;
        p = p.parentElement;
      }
      groups.push({ parent: p || img.parentElement, items: [img] });
    });
    return groups.filter(function (g) { return g.items.length > 1; });
  }

  function srcOf(img) { return img.getAttribute('src') || img.currentSrc || ''; }

  function init(root) {
    groupsIn(root).forEach(function (group) {
      var items = group.items;

      // Whichever artwork appears exactly once at load is the selected state.
      var counts = {};
      items.forEach(function (img) {
        var s = srcOf(img);
        counts[s] = (counts[s] || 0) + 1;
      });
      var srcs = Object.keys(counts);
      if (srcs.length !== 2) return; // not a selectable set — leave it alone
      var activeSrc = srcs.filter(function (s) { return counts[s] === 1; })[0];
      var inactiveSrc = srcs.filter(function (s) { return s !== activeSrc; })[0];
      if (!activeSrc || !inactiveSrc) return;

      items.forEach(function (img, i) {
        // These are images being used as controls, so they need the semantics of one.
        img.setAttribute('role', 'button');
        img.setAttribute('tabindex', '0');
        img.setAttribute('aria-pressed', srcOf(img) === activeSrc ? 'true' : 'false');
        if (!img.getAttribute('alt')) img.setAttribute('alt', 'Select attachment ' + (i + 1));
        img.style.cursor = 'pointer';

        function select() {
          var copy = copyData();
          if (copy && copy[i]) applyCopy(findPanel(), copy[i]);
          items.forEach(function (other) {
            var on = other === img;
            // srcset would override src on a <picture>, so it has to go too.
            other.removeAttribute('srcset');
            var pic = other.parentElement;
            if (pic && pic.tagName === 'PICTURE') {
              [].slice.call(pic.querySelectorAll('source')).forEach(function (s) { s.remove(); });
            }
            other.setAttribute('src', on ? activeSrc : inactiveSrc);
            other.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }

        img.addEventListener('click', select);
        img.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); }
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Re-init when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
